/* Collection card linked swatches. One fetch per group tag per page load. */
(() => {
  if (customElements.get('card-linked-swatches')) return;

  const MAX_SWATCHES = 5;
  const groupCache = new Map();

  // The raw group view outputs large images; ask Shopify's CDN for a thumbnail.
  const thumb = (src) => {
    if (!src) return null;
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', '80');
      return u.toString();
    } catch (e) {
      return src;
    }
  };

  const loadGroup = (group) => {
    if (!groupCache.has(group)) {
      const url = `/collections/all/${encodeURIComponent(group)}?view=linked-swatches-raw`;
      groupCache.set(
        group,
        fetch(url)
          .then((res) => (res.ok ? res.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return [...doc.querySelectorAll('.swatch-element')]
              .map((el) => {
                const link = el.querySelector('a');
                const img = el.querySelector('img');
                const name =
                  (el.querySelector('.data-tooltipm')?.textContent || '').trim() ||
                  el.querySelector('label')?.getAttribute('aria-label') || '';
                return {
                  handle: el.dataset.product,
                  url: link ? link.getAttribute('href') : null,
                  img: img ? thumb(img.getAttribute('src') || img.getAttribute('data-src')) : null,
                  name,
                  onSale: el.dataset.onsale === 'true',
                };
              })
              .filter((s) => s.handle && s.url && s.img);
          })
          .catch(() => [])
      );
    }
    return groupCache.get(group);
  };

  class CardLinkedSwatches extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      // Watch the whole product card: this element is empty (zero-size) until
      // it renders, and an empty element never reports as intersecting.
      const target = this.closest('product-block') || this.parentElement || this;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.io.disconnect();
            this.render();
          }
        }, { rootMargin: '400px 0px' });
        this.io.observe(target);
      } else {
        this.render();
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }

    async render() {
      this.dataset.loaded = 'true';
      const swatches = await loadGroup(this.dataset.group);
      if (swatches.length < 2) {
        this.dataset.state = 'none';
        return;
      }

      const current = this.dataset.handle;
      // Fixed order (mirrors the PDP): full-price colors A-Z by color name, then sale colors A-Z.
      // Every card in a group shows the same order; the card's own color is highlighted in place.
      const nameOf = (s) => (s.name || s.handle || '').trim().toLowerCase();
      const byName = (a, b) => nameOf(a).localeCompare(nameOf(b), undefined, { numeric: true });
      const ordered = [
        ...swatches.filter((s) => !s.onSale).sort(byName),
        ...swatches.filter((s) => s.onSale).sort(byName),
      ];

      const frag = document.createDocumentFragment();
      ordered.slice(0, MAX_SWATCHES).forEach((s) => {
        const a = document.createElement('a');
        a.className = 'card-swatch' + (s.handle === current ? ' card-swatch--active' : '');
        a.href = s.url;
        a.title = s.name;
        a.setAttribute('aria-label', s.name ? `Color: ${s.name}` : 'View color');
        if (s.handle === current) a.setAttribute('aria-current', 'true');
        const img = document.createElement('img');
        img.src = s.img;
        img.alt = '';
        img.width = 26;
        img.height = 26;
        img.loading = 'lazy';
        a.appendChild(img);
        frag.appendChild(a);
      });

      if (ordered.length > MAX_SWATCHES) {
        const more = document.createElement('span');
        more.className = 'card-swatches__more';
        more.textContent = `+${ordered.length - MAX_SWATCHES}`;
        frag.appendChild(more);
      }

      this.replaceChildren(frag);
      this.dataset.state = 'ready';
    }
  }

  customElements.define('card-linked-swatches', CardLinkedSwatches);
})();

/*
 * [Fix 2026-10-09] Quick Shop from "Complete the Look" on product pages.
 *
 * 1) Missing layout styles. Quick Shop loads the product's page and shows its
 *    product section in a panel. That section's layout CSS lives in
 *    inline <style> tags scoped to .product-form-<section id>, and the id differs
 *    per product template. If the recommended product uses a different template
 *    than the page you're on, none of its layout rules exist on the page and the
 *    popup renders unstyled. This copies those style blocks in, once per page.
 *
 * 2) Carousel Quick Shop layout: the panel under product carousels was squeezed
 *    into a narrow column. Layout CSS below widens it (carousel rows only).
 *
 * 3) Page jump. The theme scrolls the page to the clicked card when Quick Shop
 *    opens/closes. Complete the Look shows Quick Shop as a centered popup, so that
 *    scroll only made the page jump behind it. Suppressed for that block only.
 */
(() => {
  if (window.__qbCompleteTheLookFix) return;
  window.__qbCompleteTheLookFix = true;

  const WRAPPER = '.complementary-products-wrapper';
  const addedStyles = new Set();
  const pending = new Map();

  const addStylesFrom = (url) => {
    if (!url) return Promise.resolve();
    if (pending.has(url)) return pending.get(url);
    const p = fetch(url)
      .then((res) => (res.ok ? res.text() : ''))
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const pageStyles = [...document.querySelectorAll('style')].map((s) => s.textContent);
        doc.querySelectorAll('.section-main-product style').forEach((el) => {
          const css = el.textContent;
          if (!css || addedStyles.has(css)) return;
          addedStyles.add(css);
          if (pageStyles.includes(css)) return;
          const st = document.createElement('style');
          st.dataset.quickbuyStyles = '';
          st.textContent = css;
          document.head.appendChild(st);
        });
      })
      .catch(() => {});
    pending.set(url, p);
    return p;
  };

  // Start loading the product's own styles as soon as any Quick Shop starts
  // opening (Complete the Look popup, carousel rows, collection grid).
  document.addEventListener('on:quickbuy:before-open', (evt) => {
    const cont = evt.target;
    if (!cont || !cont.closest) return;
    const toggle =
      cont.closest('.product-block')?.querySelector('.quickbuy-toggle') ||
      cont.closest('.collection-slider-row')?.querySelector('.product-block.expanded .quickbuy-toggle');
    addStylesFrom(toggle && toggle.href);
  });

  // Layout for the Quick Shop panel under product carousels.
  const layoutCss = "/* [Fix 2026-10-09] Quick Shop panel that opens under product carousels\n   (featured collection / product list rows). The panel was squeezed into a\n   ~500px column, so the title wrapped one word per line, the price and\n   Add to Cart were cut off and sizes stacked vertically. Scoped to carousel\n   rows only, so the cart drawer and popup Quick Shops are untouched. */\n.collection-slider-row > .quickbuy-container .quickbuy-inner-wrapper,\n.collection-slider-row > .quickbuy-container .inner {\n  max-width: none !important;\n  width: 100% !important;\n}\n.collection-slider-row > .quickbuy-container .quickbuy-content.product-info {\n  width: 100%;\n  max-width: 1100px;\n  margin-inline: auto;\n  box-sizing: border-box;\n  overflow: visible;\n}\n.collection-slider-row > .quickbuy-container .quickbuy-content .product-title {\n  font-size: 26px !important;\n  line-height: 1.2 !important;\n  overflow-wrap: normal;\n  word-break: normal;\n}\n@media screen and (min-width: 768px) {\n  .collection-slider-row > .quickbuy-container .quickbuy-content.product-info {\n    display: flex;\n    align-items: flex-start;\n    justify-content: center;\n  }\n  .collection-slider-row > .quickbuy-container .quickbuy-content .product-column-left {\n    flex: 0 0 auto;\n    width: 42% !important;\n  }\n  .collection-slider-row > .quickbuy-container .quickbuy-content .product-column-right {\n    flex: 0 0 auto;\n    width: calc(58% - 32px) !important;\n    max-width: none !important;\n    margin-inline-start: 32px !important;\n  }\n}\n@media screen and (max-width: 767px) {\n  .collection-slider-row > .quickbuy-container .quickbuy-content .product-title {\n    font-size: 20px !important;\n  }\n}";
  const layoutStyle = document.createElement('style');
  layoutStyle.dataset.quickbuyLayout = '';
  layoutStyle.textContent = layoutCss;
  document.head.appendChild(layoutStyle);

  // Block the theme's page scroll for clicks that open/close this popup.
  let suppressUntil = 0;
  const originalScrollTo = window.scrollTo.bind(window);
  window.scrollTo = function (...args) {
    if (Date.now() < suppressUntil) return;
    return originalScrollTo(...args);
  };
  document.addEventListener(
    'click',
    (evt) => {
      const el = evt.target && evt.target.closest
        ? evt.target.closest(`${WRAPPER} .quickbuy-toggle, ${WRAPPER} .close-detail, ${WRAPPER} .customCloseBtn`)
        : null;
      if (el) suppressUntil = Date.now() + 400;
    },
    true
  );
})();

/*
 * [Fix 2026-10-09] Quick Shop inside Nosto recommendation blocks.
 *
 * Nosto renders its cards with the theme's Quick Shop button, so the theme
 * opened the Quick Shop panel *inside that one card*: squeezed to the card's
 * width, title one word per line, price and Add to Cart cut off. For Quick Shop
 * buttons inside Nosto blocks, this opens the product in a centered popup
 * instead (same idea as Complete the Look). Nosto's search/collection grid and
 * Complete the Look keep their existing behaviour.
 */
(() => {
  if (window.__nostoQuickShopPopup) return;
  window.__nostoQuickShopPopup = true;

  const NOSTO = '.nosto_element, nosto-dynamic-card, [id^="nosto"], [class*="nosto"]';
  const SKIP = '.nosto-serp-content, .complementary-products-wrapper, .cart-item-upsells';

  const style = document.createElement('style');
  style.dataset.nostoQuickShop = '';
  style.textContent = `
    .nqs-overlay { position: fixed; inset: 0; z-index: 2147483000; display: none; align-items: center; justify-content: center; padding: 15px; background: rgba(0,0,0,.5); }
    .nqs-overlay.is-open { display: flex; }
    html.nqs-lock { overflow: hidden; }
    .nqs-dialog { position: relative; width: 100%; max-width: 900px; max-height: 90vh; overflow-y: auto; background: #fff; border-radius: 8px; box-shadow: 0 0 20px rgba(0,0,0,.3); padding: 48px 28px 32px; box-sizing: border-box; }
    .nqs-close { position: absolute; top: 8px; right: 8px; z-index: 2; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: #fff; border: 0; cursor: pointer; padding: 0; color: #000; }
    .nqs-close svg { width: 28px; height: 28px; }
    .nqs-loading { min-height: 200px; display: flex; align-items: center; justify-content: center; }
    .nqs-dialog .quickbuy-content { margin: 0 !important; padding: 0 !important; max-width: none !important; width: 100% !important; }
    .nqs-dialog .product-title { font-size: 26px !important; line-height: 1.2 !important; }
    @media screen and (min-width: 768px) {
      .nqs-dialog .quickbuy-content.product-info { display: flex; align-items: flex-start; }
      .nqs-dialog .quickbuy-content .product-column-left { flex: 0 0 auto; width: 45% !important; }
      .nqs-dialog .quickbuy-content .product-column-right { flex: 0 0 auto; width: calc(55% - 32px) !important; max-width: none !important; margin-inline-start: 32px !important; }
    }
    @media screen and (max-width: 767px) {
      .nqs-dialog { padding: 44px 15px 20px; }
      .nqs-dialog .product-title { font-size: 20px !important; }
    }
  `;
  document.head.appendChild(style);

  let overlay = null;
  let content = null;
  let controller = null;
  let lastFocus = null;
  const addedStyles = new Set();

  const close = () => {
    if (!overlay || !overlay.classList.contains('is-open')) return;
    if (controller) controller.abort();
    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('nqs-lock');
    content.innerHTML = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };

  const build = () => {
    overlay = document.createElement('div');
    overlay.className = 'nqs-overlay';
    overlay.innerHTML =
      '<div class="nqs-dialog" role="dialog" aria-modal="true" aria-label="Quick shop">' +
      '<button type="button" class="nqs-close" aria-label="Close">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
      '</button><div class="nqs-body"></div></div>';
    document.body.appendChild(overlay);
    content = overlay.querySelector('.nqs-body');
    overlay.querySelector('.nqs-close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    // Close once the product is added, so the cart drawer / notification shows.
    document.addEventListener('on:cart:add', close);
    document.addEventListener('on:cart-drawer:before-open', close);
    // Picking another color in the popup loads that colorway in the popup.
    content.addEventListener('click', (e) => {
      const link = e.target.closest ? e.target.closest('.linked-product-swatches a[href]') : null;
      if (!link) return;
      e.preventDefault();
      open(link.getAttribute('href'), lastFocus);
    });
  };

  const prepare = (tmpl, url) => {
    const found = tmpl.content.querySelector('.quickbuy-content');
    if (!found) return null;
    const detail = found.cloneNode(true);
    detail.querySelectorAll('.more').forEach((el) => { el.href = url; });
    detail.querySelectorAll('.product-title').forEach((el) => {
      el.innerHTML = `<a href="">${el.innerHTML}</a>`;
      el.firstElementChild.href = url;
    });
    detail.querySelectorAll('.show-gallery').forEach((el) => { el.classList.remove('show-gallery'); el.href = url; });
    detail.querySelectorAll('.not-in-quickbuy').forEach((el) => el.remove());
    detail.querySelectorAll('.only-in-quickbuy').forEach((el) => el.classList.remove('only-in-quickbuy'));
    detail.querySelectorAll('.sticky-content-container').forEach((el) => el.classList.remove('sticky-content-container'));
    detail.querySelectorAll('pickup-availability').forEach((el) => el.remove());
    detail.querySelectorAll('variant-picker[data-update-url="true"]').forEach((el) => { el.dataset.updateUrl = false; });
    detail.querySelectorAll('noscript').forEach((el) => el.remove());
    detail.querySelectorAll('.media-gallery .main-image carousel-slider').forEach((el) => el.classList.remove('mobile-only'));
    detail.querySelectorAll('.product-media-collage').forEach((el) => el.remove());
    detail.querySelectorAll('.media-gallery .thumbnails').forEach((el) => el.classList.remove('mobile-only'));
    [
      'media-gallery--layout-carousel-beside',
      'media-gallery--layout-columns-1',
      'media-gallery--layout-columns-2',
      'media-gallery--layout-collage-1',
      'media-gallery--layout-collage-2',
    ].forEach((cl) => {
      detail.querySelectorAll(`.${cl}`).forEach((el) => {
        el.classList.remove(cl);
        el.classList.add('media-gallery--layout-carousel-under');
      });
    });

    // Stylesheets, scripts and inline styles the product section needs.
    tmpl.content.querySelectorAll('.section-main-product link[rel="stylesheet"]').forEach((el) => {
      if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
        document.head.insertAdjacentHTML('beforeend', el.outerHTML);
      }
    });
    tmpl.content.querySelectorAll('.section-main-product script[src]').forEach((el) => {
      if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
        const s = document.createElement('script');
        s.src = el.getAttribute('src');
        s.defer = true;
        document.head.appendChild(s);
      }
    });
    const pageStyles = [...document.querySelectorAll('style')].map((s) => s.textContent);
    tmpl.content.querySelectorAll('.section-main-product style').forEach((el) => {
      const css = el.textContent;
      if (!css || addedStyles.has(css)) return;
      addedStyles.add(css);
      if (pageStyles.includes(css)) return;
      const st = document.createElement('style');
      st.dataset.quickbuyStyles = '';
      st.textContent = css;
      document.head.appendChild(st);
    });
    return detail;
  };

  const open = (url, trigger) => {
    if (!overlay) build();
    if (controller) controller.abort();
    controller = new AbortController();
    lastFocus = trigger;
    content.innerHTML = '<div class="nqs-loading"><div class="loading-spinner"></div></div>';
    overlay.classList.add('is-open');
    document.documentElement.classList.add('nqs-lock');
    overlay.querySelector('.nqs-close').focus();

    fetch(url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;
        const detail = prepare(tmpl, url);
        if (!detail) throw new Error('No quick shop content');
        content.innerHTML = '';
        content.appendChild(detail);
        document.dispatchEvent(new CustomEvent('nqs:after-open', { detail: { root: content } }));
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        // Could not load: go to the product page instead.
        window.location.href = url;
      });
  };

  // Capture phase, so the theme's own handler (which would open the squeezed
  // in-card panel) never sees clicks on Nosto Quick Shop buttons.
  document.addEventListener(
    'click',
    (evt) => {
      const toggle = evt.target && evt.target.closest ? evt.target.closest('.quickbuy-toggle') : null;
      if (!toggle) return;
      if (!toggle.closest(NOSTO) || toggle.closest(SKIP)) return;
      const url = toggle.getAttribute('href') || toggle.dataset.url || toggle.closest('[data-url]')?.dataset.url;
      if (!url || url === '#') return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      open(url, toggle);
    },
    true
  );
})();

/*
 * [Fix 2026-10-09] Color swatches in Quick Shop.
 *
 * The product page fills its color swatches (separate products linked by a
 * "group-" tag) with an inline script in snippets/product-linked-swatches.liquid.
 * Quick Shop copies the product section into a panel, and copied scripts don't
 * run, so the swatch row stayed empty. This fills it the same way, in the same
 * order, whenever any Quick Shop opens (theme panels and the Nosto popup).
 */
(() => {
  if (window.__qbLinkedSwatches) return;
  window.__qbLinkedSwatches = true;

  // Color name comes from custom.color_name (the label's aria-label); falls back to the handle.
  const colorName = ($item) => {
    const $label = $item.querySelector('label[aria-label]');
    const name = $label ? $label.getAttribute('aria-label').trim() : '';
    return (name || $item.dataset.product || '').toLowerCase();
  };
  const byColorName = (a, b) => colorName(a).localeCompare(colorName(b), undefined, { numeric: true });

  const fill = (root) => {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.linked-product-swatches').forEach(($linked) => {
      const $target = $linked.querySelector('.swatch');
      if (!$target || $linked.dataset.qbFilled || $target.children.length) return;
      $linked.dataset.qbFilled = 'true';
      const groupTag = $linked.dataset.grouptag;
      const current = $linked.dataset.currentproduct;
      if (!groupTag) return;
      fetch(`/collections/all/${groupTag}?view=linked-swatches-raw`)
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          let $swatches = [...doc.body.querySelectorAll('.swatch-element')];
          // Same ordering as the product page script: full-price A-Z, then sale A-Z.
          const regular = [];
          const onsaleList = [];
          $swatches.forEach(($item) => {
            let onsale = false;
            try { onsale = JSON.parse($item.dataset.onsale); } catch (e) {}
            onsale ? onsaleList.push($item) : regular.push($item);
          });
          $swatches = [...regular.sort(byColorName), ...onsaleList.sort(byColorName)];
          $swatches.forEach(($s) => $target.insertAdjacentElement('beforeend', $s));
          // Highlight the current color in place (do not move it).
          const $cur = $swatches.find(($item) => $item.dataset.product == current);
          if ($cur) $cur.classList.add('swatch--active');
        })
        .catch(() => {});
    });
  };

  document.addEventListener('on:quickbuy:after-open', (evt) => fill(evt.target));
  document.addEventListener('nqs:after-open', (evt) => fill(evt.detail && evt.detail.root));
})();
